import React, { useState } from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";

export default function AuthModal({ onClose, onSuccess }) {
  const { signIn, signUp, signInAnonymously } = useAuth();
  const [mode, setMode]         = useState("login"); // "login" | "signup"
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await signIn(email, password);
        onSuccess && onSuccess();
      } else {
        const data = await signUp(email, password);
        // If Supabase requires email confirmation, no session is returned yet.
        if (!data?.session) {
          setEmailSent(true);
        } else {
          onSuccess && onSuccess();
        }
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  async function handleOAuth(provider) {
    setError("");
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin },
    });
    if (error) setError(error.message);
  }

  async function handleAnonymous() {
    setError("");
    setLoading(true);
    try {
      await signInAnonymously();
      onSuccess && onSuccess();
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  if (emailSent) {
    return (
      <div className="stats-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="stats-modal auth-modal">
          <div className="stats-header">
            <h2>Check your email</h2>
            <button className="stats-close" onClick={onClose}>✕</button>
          </div>
          <div style={{ padding: "8px 4px 4px", lineHeight: 1.55 }}>
            <p style={{ margin: "0 0 12px" }}>
              We sent a confirmation link to <strong>{email}</strong>.
            </p>
            <p style={{ margin: "0 0 12px" }}>
              Click the link in that email to verify your account, then come back and sign in.
            </p>
            <p style={{ margin: "0 0 16px", fontSize: "0.9em", opacity: 0.75 }}>
              Don't see it? Check your spam folder. The email comes from <em>noreply@mail.app.supabase.io</em>.
            </p>
            <button
              className="button"
              style={{ width: "100%" }}
              onClick={() => { setEmailSent(false); setMode("login"); setPassword(""); }}
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stats-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="stats-modal auth-modal">
        <div className="stats-header">
          <h2>{mode === "login" ? "Sign In" : "Create Account"}</h2>
          <button className="stats-close" onClick={onClose}>✕</button>
        </div>

        <p className="auth-subtitle">
          {mode === "login"
            ? "Sign in to submit scores to the leaderboard."
            : "Create an account to join the leaderboard. We'll email you a confirmation link."}
        </p>

        {/* OAuth buttons */}
        <div className="auth-oauth-row">
          <button className="auth-oauth-btn auth-oauth-google" onClick={() => handleOAuth("google")}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>
        </div>

        <div className="auth-divider"><span>or</span></div>

        <form className="auth-form" onSubmit={handleSubmit}>
          <input
            className="auth-input"
            type="email"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoFocus
          />
          <input
            className="auth-input"
            type="password"
            placeholder="Password (min 6 chars)"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            minLength={6}
          />
          {error && <div className="auth-error">{error}</div>}
          <button className="button" type="submit" disabled={loading}>
            {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Sign Up"}
          </button>
        </form>

        <div className="auth-switch">
          {mode === "login" ? (
            <>Don't have an account?{" "}
              <button className="auth-link" onClick={() => { setMode("signup"); setError(""); }}>
                Sign up
              </button>
            </>
          ) : (
            <>Already have an account?{" "}
              <button className="auth-link" onClick={() => { setMode("login"); setError(""); }}>
                Sign in
              </button>
            </>
          )}
        </div>

        <div className="auth-divider"><span>just want a username?</span></div>

        <button
          className="auth-oauth-btn"
          onClick={handleAnonymous}
          disabled={loading}
          style={{ width: "100%" }}
        >
          🎭 Play without email
        </button>
      </div>
    </div>
  );
}
